import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { FormControl, Grid, Input, InputLabel } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  DateFormat,
  ErrorTooltip,
  type Field,
  moment,
  type Nil,
  PermissionArea,
  PuiSelect,
  PuiTextField,
  Text,
} from '@pbt/pbt-ui-components'

import AddToAppointmentCard from '~/components/common/addToAppointment/AddToAppointmentCard'
import ContactTargetSlot from '~/components/common/ContactTargetSlot'
import UserSelect, {
  UserSelectFilterScope,
} from '~/components/common/inputs/UserSelect'
import NotesTemplateInput from '~/components/common/NotesTemplateInput'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import RoleName from '~/constants/roleNames'
import { useCreatableTaskTypes } from '~/store/hooks/tasks'
import {
  getCRUDByArea,
  getCurrentBusinessWellnessPlansEnabled,
} from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { type ContactSlot, type Task, TeamFilter } from '~/types'
import useDialog from '~/utils/useDialog'
import useHasRole from '~/utils/useHasRole'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import TaskRecurrenceSettings from '../TaskRecurrenceSettings'

import '@chewy/kib-tag-styles/dist/kib-tag.css'

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    detailsRow: {
      borderBottom: theme.constants.outlinedInputBorder,
    },
    listbox: {
      maxHeight: 190,
    },
  }),
  { name: 'TaskComponent' },
)

interface TaskFormV1Props {
  assigneeClientAndPatientIsValid: boolean
  dueDate: string | Nil
  fields: {
    assigneeId: Field
    clientNotes: Field
    forClient: Field
    instructions: Field
    name: Field
    notes: Field
    typeId: Field
  }
  instructionsRef: any
  notesRef: any
  rootRef: any
  selectedAppointmentId: string | null
  selectedClientId: string | Nil
  selectedPatientId: string | Nil
  setSelectedAppointmentId: any
  setSlotInfo: (info: ContactSlot) => void
  shouldUseVCRBehavior: boolean | undefined
  slotInfo: ContactSlot
  soapId: string | undefined
  task: Task | undefined
  taskRecurrenceSettingsRef: any
  touched: boolean
}

export const TaskFormV1 = ({
  assigneeClientAndPatientIsValid,
  instructionsRef,
  notesRef,
  rootRef,
  taskRecurrenceSettingsRef,
  selectedAppointmentId,
  selectedClientId,
  selectedPatientId,
  setSelectedAppointmentId,
  soapId,
  shouldUseVCRBehavior,
  task,
  fields: {
    assigneeId,
    forClient,
    instructions,
    clientNotes,
    name,
    notes,
    typeId,
  },
  setSlotInfo,
  slotInfo,
  touched,
  dueDate: dueDateProp,
}: TaskFormV1Props) => {
  const classes = useStyles()

  const isContextItem = useIsCurrentContextItem(task)
  const { t } = useTranslation(['Tasks', 'Tooltips', 'Common'])

  const wellnessPlansEnabled = useSelector(
    getCurrentBusinessWellnessPlansEnabled,
  )
  const taskPermissions = useSelector(getCRUDByArea(PermissionArea.TASK))
  const isCvcRolesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_ROLES),
  )
  const isCurrentUserVCR = useHasRole(RoleName.VCR)

  const CreatableTaskTypes = useCreatableTaskTypes()
  const [openAddToAppointmentDialog, closeAddToAppointmentDialog] = useDialog(
    DialogNames.ADD_TO_APPOINTMENT,
  )

  const onAppointmentSelected = (newAppointmentId: string) => {
    closeAddToAppointmentDialog()
    setSelectedAppointmentId(newAppointmentId)
  }

  const onAddToAppointmentRequested = () => {
    openAddToAppointmentDialog({
      clientId: selectedClientId,
      patientId: selectedPatientId,
      title: t('Dialogs:ADD_TASK_TO_APPOINTMENT.TITLE'),
      onAdd: onAppointmentSelected,
      filterByClient: true,
    })
  }

  const showAssignClientAndPatientErrorMessage =
    touched && !assigneeClientAndPatientIsValid
  const isCreate = !task?.id
  return (
    <>
      <Grid container item columnSpacing={3}>
        <Grid item xs={6}>
          <PuiTextField
            disabled={
              !isContextItem || (!taskPermissions.update && isCvcRolesEnabled)
            }
            field={name}
            inputProps={{ maxLength: 100 }}
            label={`${name.label}*`}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel htmlFor="task-type-select">{typeId.label}</InputLabel>
            <PuiSelect
              disabled={
                !isContextItem || (!taskPermissions.update && isCvcRolesEnabled)
              }
              field={typeId}
              input={<Input id="task-type-select" />}
              items={CreatableTaskTypes}
              renderEmpty={false}
            />
          </FormControl>
        </Grid>
      </Grid>
      <TaskRecurrenceSettings
        appointmentId={selectedAppointmentId}
        disabled={!taskPermissions.update && isCvcRolesEnabled}
        dueDate={dueDateProp}
        ref={taskRecurrenceSettingsRef}
        task={task}
      />
      <ErrorTooltip
        message={
          !selectedClientId || !selectedPatientId
            ? t('Tooltips:CLIENT_AND_PATIENT_IS_REQUIRED')
            : t('Tooltips:CLIENT_NEEDS_TO_BE_A_BOOP_SYSTEM_USER')
        }
        open={showAssignClientAndPatientErrorMessage}
      >
        <Grid container mt={2}>
          <ContactTargetSlot
            anchorRef={rootRef}
            disabled={
              !isContextItem || (!taskPermissions.update && isCvcRolesEnabled)
            }
            showAddContact={!forClient.value}
            onSlotChange={setSlotInfo}
            {...slotInfo}
          />
        </Grid>
      </ErrorTooltip>
      {selectedClientId && selectedPatientId && isContextItem && (
        <Grid container item mt={2}>
          <AddToAppointmentCard
            appointmentId={selectedAppointmentId}
            readOnly={!taskPermissions.update && isCvcRolesEnabled}
            onAddToAppointmentRequested={onAddToAppointmentRequested}
            onEdit={onAddToAppointmentRequested}
          />
        </Grid>
      )}
      {!forClient.value && (
        <Grid container item mt={2}>
          <Grid item lg={5} md={8} sm={12}>
            <UserSelect
              classes={{
                listbox: classes.listbox,
              }}
              disabled={shouldUseVCRBehavior && !isCurrentUserVCR}
              field={assigneeId}
              filterScope={UserSelectFilterScope.Staff}
              label={assigneeId.label}
              readOnly={
                !isContextItem || (!taskPermissions.update && isCvcRolesEnabled)
              }
              teamFilter={TeamFilter.ALL}
            />
          </Grid>
        </Grid>
      )}
      <Grid container item mt={3}>
        <Text strong variant="body2">
          {forClient.value ? t('Common:INTERNAL_NOTES') : t('Common:NOTES')}
        </Text>
        <NotesTemplateInput
          hidePanel
          disabled={!taskPermissions.update && isCvcRolesEnabled}
          field={notes}
          isSoap={Boolean(soapId)}
          minHeight={2}
          ref={notesRef}
          soapId={soapId}
        />
      </Grid>
      {wellnessPlansEnabled && forClient.value && (
        <Grid container item mt={3}>
          <Text strong variant="body2">
            {instructions.label}
          </Text>
          <NotesTemplateInput
            hidePanel
            disabled={!taskPermissions.update && isCvcRolesEnabled}
            field={instructions}
            isSoap={Boolean(soapId)}
            minHeight={2}
            ref={instructionsRef}
            soapId={soapId}
          />
        </Grid>
      )}
      {!isCreate && (
        <>
          {wellnessPlansEnabled && task?.completionDate && (
            <Grid container item className={classes.detailsRow} mt={2} py={1}>
              <Grid item xs={3}>
                <Text strong variant="body2">
                  {t('Tasks:LABEL.TASK_COMPLETED')}
                </Text>
              </Grid>
              <Grid item xs>
                <Text variant="body2">
                  {moment(task?.completionDate).format(
                    DateFormat.FULL_DATE_TIME_AT,
                  )}
                </Text>
              </Grid>
            </Grid>
          )}
          {wellnessPlansEnabled && clientNotes.value && (
            <Grid container item className={classes.detailsRow} py={1}>
              <Grid item xs={3}>
                <Text strong variant="body2">
                  {clientNotes.label}
                </Text>
              </Grid>
              <Grid item xs>
                <Text variant="body2">{clientNotes.value}</Text>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </>
  )
}
